import { AsyncPipe } from '@angular/common';
import { HttpStatusCode } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { AuthenticationService } from '@modules/authentication/services/authentication.service';
import { Store } from '@ngrx/store';
import { loadAppInitializationData } from '@shared/utils/app-initialization';
import { TuiButton, TuiNotificationService } from '@taiga-ui/core';
import { TuiButtonLoading } from '@taiga-ui/kit';
import { TuiBlockStatus } from '@taiga-ui/layout';
import { BehaviorSubject, filter, first } from 'rxjs';
import { AppConfigHttpService } from 'src/app/services/http/app-config.http.service';
import { ServerStatusHttpService } from 'src/app/services/http/server-status.http.service';
import { Status } from 'src/app/shared/models/enums/status.enum';
import { lifeCycleActions } from 'src/app/shared/store/life-cycle/life-cycle.actions';
import { LoaderComponent } from '../loader/loader.component';

@Component({
  templateUrl: './server-unavailable.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [AsyncPipe, TranslocoDirective, LoaderComponent, TuiBlockStatus, TuiButton, TuiButtonLoading],
  host: {
    class: 'block h-screen px-4',
  },
})
export class ServerUnavailableComponent implements OnInit {
  Status = Status;
  isStatusChecking$ = new BehaviorSubject(false);

  private readonly router = inject(Router);
  private readonly serverStatusService = inject(ServerStatusHttpService);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly appConfigService = inject(AppConfigHttpService);
  private readonly store = inject(Store);
  private readonly alertService = inject(TuiNotificationService);
  private readonly translocoService = inject(TranslocoService);

  ngOnInit(): void {
    this.store.dispatch(lifeCycleActions.resetState());
    this.retryConnection();
  }

  retryConnection(): void {
    this.isStatusChecking$
      .pipe(
        first(),
        filter((isStatusChecking) => !isStatusChecking),
      )
      .subscribe(() => {
        this.isStatusChecking$.next(true);
        this.serverStatusService.getStatus().subscribe({
          next: async (pingResult) => {
            if (pingResult.status === HttpStatusCode.Ok) {
              await loadAppInitializationData(this.appConfigService, this.authenticationService);
              this.router.navigate([''], { queryParamsHandling: 'merge' });
            }
          },
          error: () => {
            this.alertService
              .open(this.translocoService.translate("Impossible d'accéder au service..."), {
                label: this.translocoService.translate('Service indisponible'),
                appearance: 'negative',
              })
              .subscribe();
            this.isStatusChecking$.next(false);
          },
        });
      });
  }
}
